import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useGeneralSettings } from '../contexts/GeneralSettingsContext';
import { useCurrentUser } from '../hooks/useCurrentUser';

/**
 * StatusGuard — decides whether to render the site or redirect to
 * /coming-soon when the admin has toggled the site inactive.
 *
 * IMPORTANT: We DO NOT block children behind a fullscreen loader while
 * general_settings are being fetched. Blocking made every route show a
 * blank cream page for ~300–800ms before the actual layout appeared,
 * which users perceived as "empty first, then products load". Instead
 * we render children immediately so each page can display its own
 * skeleton (see ShopPage → ProductCardSkeleton). The coming-soon
 * redirect is only evaluated AFTER settings finish loading, so nothing
 * flickers into coming-soon for active sites.
 */
const StatusGuard = ({ children }) => {
  const location = useLocation();
  const { settings, loading } = useGeneralSettings();
  const { isAdmin } = useCurrentUser();

  // Admin routes are always allowed (bypasses the coming-soon check).
  if (location.pathname.startsWith('/admin')) {
    return children;
  }

  // Only redirect once we know for sure the site is inactive.
  // While `loading` is true we optimistically render children — the
  // storefront then shows its own per-page skeletons which is a much
  // better perceived-loading experience than a blank splash.
  if (!loading && !settings?.status?.isActive && !isAdmin) {
    return <Navigate to="/coming-soon" replace />;
  }

  return children;
};

export default StatusGuard;
